import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import {
  ArrowRight, BadgeCheck, Building2, CalendarDays, Check, Download, FileArchive, Globe,
  Image as IconaImmagine, Mail, MapPin, Newspaper, Palette, Phone, Quote, ShieldCheck,
  Type, Users, X
} from 'lucide-react';
import CopiaTesto from '../components/pressKit/CopiaTesto';
import { pressKit, tappePressKit } from '../data/pressKit';
import { ANNO_FONDAZIONE, anniDallaFondazione } from '../utils/anniUnasca';
import { fileBadge, FORMATI } from '../utils/badgeAssociati';
import { isAuthenticated as associatoAutenticato } from '../services/otpAuthService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

const CARD = 'rounded-2xl bg-white dark:bg-gray-900 shadow-sm ring-1 ring-black/5 dark:ring-white/10';

const SEZIONI = [
  { id: 'sintesi', etichetta: 'In sintesi' },
  { id: 'descrizioni', etichetta: 'Descrizioni' },
  { id: 'storia', etichetta: 'Storia' },
  { id: 'identita', etichetta: 'Loghi e colori' },
  { id: 'foto', etichetta: 'Foto' },
  { id: 'badge', etichetta: 'Badge' },
  { id: 'contatti', etichetta: 'Contatti' }
];

const Occhiello: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-primary-700 dark:text-primary-400">{children}</p>
);

const dataLeggibile = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });

/**
 * Press Kit — area stampa (/press-kit): testi pronti, dati, storia, loghi, colori,
 * foto, badge degli associati e contatto stampa.
 */
const PressKitPage: React.FC = () => {
  const [studiRete, setStudiRete] = useState<number | null>(null);

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/public/certified-network/conteggio`)
      .then(({ data }) => setStudiRete(typeof data?.data?.studi === 'number' ? data.data.studi : null))
      .catch(() => setStudiRete(null));
  }, []);

  const tappe = tappePressKit();
  const { sintesi, descrizioni, contatto } = pressKit;

  const righeSintesi: { etichetta: string; valore: React.ReactNode; Icona: React.ElementType }[] = [
    { etichetta: 'Nome', valore: sintesi.nome, Icona: Building2 },
    { etichetta: 'Fondazione', valore: `25 febbraio ${ANNO_FONDAZIONE} (${anniDallaFondazione()} anni)`, Icona: CalendarDays },
    { etichetta: 'Sede', valore: sintesi.sede, Icona: MapPin },
    { etichetta: 'Associati', valore: sintesi.professionisti, Icona: Users },
    {
      etichetta: 'Rete Certificata',
      valore: studiRete ? `${studiRete.toLocaleString('it-IT')} studi certificati` : 'Studi certificati in tutta Italia',
      Icona: ShieldCheck
    },
    {
      etichetta: 'Guida',
      valore: sintesi.guida.map((g) => `${g.nome} (${g.ruolo})`).join(' · '),
      Icona: BadgeCheck
    },
    { etichetta: 'Sito', valore: <a href={sintesi.sito} className="font-semibold text-primary-700 hover:underline dark:text-primary-400">studi.unasca.it</a>, Icona: Globe },
    { etichetta: 'Contatto stampa', valore: <a href={`mailto:${contatto.email}`} className="font-semibold text-primary-700 hover:underline dark:text-primary-400">{contatto.email}</a>, Icona: Mail }
  ];

  const ctaBadge = associatoAutenticato() ? '/area-riservata/badge' : '/accesso-associati';

  return (
    <>
      <Helmet>
        <title>Press Kit — Area stampa UNASCA Studi</title>
        <meta
          name="description"
          content="Press Kit di UNASCA Studi: descrizioni pronte, dati, storia, loghi, colori, foto e contatto stampa per giornalisti e partner."
        />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link href="https://fonts.googleapis.com/css2?family=Caveat:wght@600&display=swap" rel="stylesheet" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-950">

        {/* ═══ HERO ═══ */}
        <section className="relative overflow-hidden bg-primary-950 text-white">
          <div className="absolute inset-y-0 right-0 w-full lg:w-[62%]">
            <img
              src="/images/press-kit/redazione.webp"
              srcSet="/images/press-kit/redazione-1280.webp 1280w, /images/press-kit/redazione.webp 2400w"
              sizes="(min-width: 1024px) 62vw, 100vw"
              alt=""
              aria-hidden="true"
              fetchPriority="high"
              className="h-full w-full object-cover object-[65%_center]"
            />
          </div>
          <div className="absolute inset-0 bg-primary-950/85 lg:bg-transparent lg:bg-gradient-to-r lg:from-primary-950 lg:from-[38%] lg:via-primary-950/75 lg:via-[52%] lg:to-transparent" />

          <div className="relative container-custom pt-14 pb-16 md:pt-20 md:pb-24">
            <div className="max-w-xl motion-safe:animate-fade-in">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 ring-1 ring-white/20 backdrop-blur-sm mb-6">
                <Newspaper className="w-4 h-4 text-primary-300" aria-hidden="true" />
                <span className="text-sm font-semibold">Area stampa</span>
              </div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4 leading-tight tracking-tight">Press Kit</h1>
              <p className="text-lg md:text-xl font-semibold text-primary-200 mb-4">
                Tutto quello che serve per raccontare UNASCA Studi
              </p>
              <p className="text-base md:text-lg text-white/80 mb-8">
                Testi pronti, dati, storia, loghi ufficiali, colori e foto. Le risorse sono libere per uso editoriale,
                citando UNASCA Studi come fonte.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={pressKit.zip}
                  download
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-6 py-3.5 text-base font-semibold text-primary-800 shadow-lg hover:bg-primary-50 transition-colors"
                >
                  <FileArchive className="w-5 h-5" aria-hidden="true" />
                  Scarica tutto (.zip)
                </a>
                <a
                  href={`mailto:${contatto.email}`}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-white/10 px-6 py-3.5 text-base font-semibold text-white ring-1 ring-white/30 backdrop-blur-sm hover:bg-white/20 transition-colors"
                >
                  <Mail className="w-5 h-5" aria-hidden="true" />
                  Contatto stampa
                </a>
              </div>
              <p className="mt-6 text-xs text-white/60">Ultimo aggiornamento: {dataLeggibile(pressKit.aggiornato)}</p>
            </div>
          </div>

          <div className="pointer-events-none absolute bottom-14 right-10 hidden -rotate-6 text-right text-white lg:block xl:right-16" aria-hidden="true">
            <span className="block text-4xl leading-tight drop-shadow-lg" style={{ fontFamily: "'Caveat', cursive" }}>
              Raccontare<br />la professione
            </span>
            <svg viewBox="0 0 220 16" className="ml-auto mt-1 w-52 text-primary-400" fill="none">
              <path d="M3 12 C 60 2, 140 2, 217 8" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
            </svg>
          </div>
        </section>

        {/* Indice */}
        <nav aria-label="Sezioni del Press Kit" className="sticky top-16 z-20 border-b border-gray-200 bg-white/90 backdrop-blur dark:border-gray-800 dark:bg-gray-950/90">
          <div className="container-custom flex gap-1.5 overflow-x-auto py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {SEZIONI.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium text-gray-600 hover:bg-primary-50 hover:text-primary-800 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                {s.etichetta}
              </a>
            ))}
          </div>
        </nav>

        {/* ═══ IN SINTESI ═══ */}
        <section id="sintesi" className="scroll-mt-32 py-16 md:py-20">
          <div className="container-custom max-w-5xl">
            <Occhiello>In sintesi</Occhiello>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-8 tracking-tight">UNASCA Studi in breve</h2>
            <dl className={`${CARD} divide-y divide-gray-100 dark:divide-gray-800`}>
              {righeSintesi.map(({ etichetta, valore, Icona }) => (
                <div key={etichetta} className="grid gap-1 px-5 py-4 sm:grid-cols-3 sm:gap-4">
                  <dt className="flex items-center gap-2 text-sm font-semibold text-gray-500 dark:text-gray-400">
                    <Icona className="h-4 w-4 text-primary-700 dark:text-primary-400" aria-hidden="true" />
                    {etichetta}
                  </dt>
                  <dd className="text-sm md:text-base text-gray-900 dark:text-white sm:col-span-2">{valore}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        {/* ═══ DESCRIZIONI ═══ */}
        <section id="descrizioni" className="scroll-mt-32 py-16 md:py-20 bg-white dark:bg-gray-900">
          <div className="container-custom max-w-5xl">
            <Occhiello>Testi pronti</Occhiello>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-3 tracking-tight">Descrizioni da usare negli articoli</h2>
            <p className="text-base text-gray-600 dark:text-gray-400 mb-8">Tre lunghezze, da copiare così come sono.</p>
            <div className="space-y-5">
              {[
                { titolo: 'In una frase', testo: descrizioni.breve },
                { titolo: 'In un paragrafo', testo: descrizioni.paragrafo },
                { titolo: 'In tre paragrafi', testo: descrizioni.estesa.join('\n\n') }
              ].map(({ titolo, testo }) => (
                <article key={titolo} className="rounded-2xl bg-gray-50 p-5 sm:p-6 ring-1 ring-black/5 dark:bg-gray-950 dark:ring-white/10">
                  <div className="mb-3 flex items-center justify-between gap-4">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">{titolo}</h3>
                    <CopiaTesto testo={testo} etichetta="Copia testo" />
                  </div>
                  {testo.split('\n\n').map((p) => (
                    <p key={p.slice(0, 24)} className="text-sm md:text-base leading-relaxed text-gray-700 dark:text-gray-300 [&:not(:last-child)]:mb-3">{p}</p>
                  ))}
                </article>
              ))}
            </div>

            <h3 className="mt-12 mb-4 text-base font-semibold text-gray-900 dark:text-white">Cosa fa UNASCA Studi</h3>
            <ul className="grid gap-3 sm:grid-cols-2">
              {pressKit.attivita.map((a) => (
                <li key={a} className="flex gap-3 rounded-xl bg-gray-50 p-4 text-sm text-gray-700 ring-1 ring-black/5 dark:bg-gray-950 dark:text-gray-300 dark:ring-white/10">
                  <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-700 dark:text-primary-400" aria-hidden="true" />
                  {a}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ═══ STORIA ═══ */}
        <section id="storia" className="scroll-mt-32 py-16 md:py-20 bg-primary-50/70 dark:bg-gray-950">
          <div className="container-custom max-w-5xl">
            <Occhiello>La storia in breve</Occhiello>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-8 tracking-tight">
              {anniDallaFondazione()} anni al servizio della professione
            </h2>
            <ol className="relative space-y-6 border-l-2 border-primary-200 pl-6 dark:border-primary-900">
              {tappe.map((t) => (
                <li key={t.titolo} className="relative">
                  <span className="absolute -left-[33px] top-1 h-4 w-4 rounded-full bg-primary-600 ring-4 ring-primary-50 dark:ring-gray-950" aria-hidden="true" />
                  <p className="text-sm font-bold text-primary-700 dark:text-primary-400 font-sans">{t.anno}</p>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">{t.titolo}</h3>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{t.testo}</p>
                </li>
              ))}
            </ol>
            <Link to="/timeline" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:underline dark:text-primary-400">
              La storia completa nella timeline <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>

            {/* Rete Certificata */}
            <div className={`${CARD} mt-12 flex flex-col sm:flex-row items-center gap-6 p-6 sm:p-8`}>
              <img src="/kit-stampa/anteprima-timbro-verde.webp" alt="Timbro della Rete Certificata UNASCA" width={128} height={128} className="h-28 w-28 flex-shrink-0" loading="lazy" />
              <div className="text-center sm:text-left">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">La Rete Certificata UNASCA</h3>
                <p className="mt-1 text-sm md:text-base text-gray-600 dark:text-gray-400">
                  Un contrassegno di Stato a tutela del cittadino: gli studi certificati ricevono un attestato del Poligrafico e
                  Zecca dello Stato con elementi anticontraffazione e sono consultabili sulla mappa pubblica.
                </p>
                <div className="mt-3 flex flex-wrap justify-center sm:justify-start gap-4 text-sm font-semibold">
                  <Link to="/rete-certificata/studi" className="text-primary-700 hover:underline dark:text-primary-400">Come funziona</Link>
                  <Link to="/rete-certificata" className="text-primary-700 hover:underline dark:text-primary-400">Mappa degli studi</Link>
                </div>
              </div>
            </div>

            {pressKit.citazioni.length > 0 && (
              <div className="mt-12 grid gap-5 md:grid-cols-2">
                {pressKit.citazioni.map((c) => (
                  <figure key={c.testo} className={`${CARD} p-6`}>
                    <Quote className="h-6 w-6 text-primary-600" aria-hidden="true" />
                    <blockquote className="mt-3 text-base text-gray-800 dark:text-gray-200">«{c.testo}»</blockquote>
                    <figcaption className="mt-3 flex items-center justify-between gap-3 text-sm text-gray-500 dark:text-gray-400">
                      <span><strong className="text-gray-900 dark:text-white">{c.autore}</strong>, {c.ruolo}</span>
                      <CopiaTesto testo={`«${c.testo}» — ${c.autore}, ${c.ruolo}`} />
                    </figcaption>
                  </figure>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* ═══ IDENTITÀ VISIVA ═══ */}
        <section id="identita" className="scroll-mt-32 py-16 md:py-20 bg-white dark:bg-gray-900">
          <div className="container-custom max-w-6xl">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
              <div>
                <Occhiello>Identità visiva</Occhiello>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white tracking-tight">Loghi, colori e caratteri</h2>
              </div>
              <a
                href={pressKit.zip}
                download
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary-700 px-5 py-3 text-sm font-semibold text-white hover:bg-primary-800 transition-colors"
              >
                <FileArchive className="h-4 w-4" aria-hidden="true" /> Scarica tutto (.zip)
              </a>
            </div>

            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {pressKit.download.map((d) => (
                <div key={d.file} className={`${CARD} overflow-hidden`}>
                  <div className={`flex h-40 items-center justify-center p-6 ${d.fondo === 'scuro' ? 'bg-primary-950' : 'bg-gray-50 dark:bg-gray-100'}`}>
                    <img src={d.anteprima || d.file} alt={d.titolo} loading="lazy" className="max-h-full max-w-full object-contain" />
                  </div>
                  <div className="flex items-center justify-between gap-3 p-4">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{d.titolo}</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{d.descrizione}</p>
                    </div>
                    <a
                      href={d.file}
                      download
                      className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-gray-700 ring-1 ring-gray-200 hover:ring-primary-300 dark:text-gray-200 dark:ring-gray-700"
                    >
                      <Download className="h-3.5 w-3.5" aria-hidden="true" /> {d.formato}
                    </a>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 grid gap-8 lg:grid-cols-2">
              <div>
                <h3 className="mb-4 flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-white">
                  <Palette className="h-4 w-4 text-primary-700 dark:text-primary-400" aria-hidden="true" /> Colori
                </h3>
                <ul className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {pressKit.colori.map((c) => (
                    <li key={c.hex} className={`${CARD} overflow-hidden`}>
                      <div className="h-16" style={{ backgroundColor: c.hex }} aria-hidden="true" />
                      <div className="p-3">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">{c.nome}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{c.uso}</p>
                        <CopiaTesto testo={c.hex.toUpperCase()} etichetta={c.hex.toUpperCase()} className="mt-2 font-mono" />
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="space-y-8">
                <div>
                  <h3 className="mb-4 flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-white">
                    <Type className="h-4 w-4 text-primary-700 dark:text-primary-400" aria-hidden="true" /> Caratteri
                  </h3>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className={`${CARD} p-4`}>
                      <p className="font-unasca text-3xl text-primary-700 dark:text-primary-400">UNASCA</p>
                      <p className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">Among</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Carattere del marchio, solo per il logo</p>
                    </div>
                    <div className={`${CARD} p-4`}>
                      <p className="text-3xl font-semibold text-gray-900 dark:text-white">Aa</p>
                      <p className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">Inter</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Titoli e testi del sito</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="mb-4 text-base font-semibold text-gray-900 dark:text-white">Come usare il logo</h3>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <ul className={`${CARD} space-y-2 p-4`}>
                      {['Lascia spazio libero attorno al logo', 'Verde su fondi chiari, bianco su fondi scuri', 'Usa i file ufficiali di questa pagina'].map((r) => (
                        <li key={r} className="flex gap-2 text-sm text-gray-700 dark:text-gray-300">
                          <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-600" aria-hidden="true" /> {r}
                        </li>
                      ))}
                    </ul>
                    <ul className={`${CARD} space-y-2 p-4`}>
                      {['Non deformarlo né ruotarlo', 'Non cambiarne i colori o aggiungere effetti', 'Non metterlo su fondi che lo rendono illeggibile'].map((r) => (
                        <li key={r} className="flex gap-2 text-sm text-gray-700 dark:text-gray-300">
                          <X className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" aria-hidden="true" /> {r}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══ FOTO ═══ */}
        <section id="foto" className="scroll-mt-32 py-16 md:py-20">
          <div className="container-custom max-w-5xl">
            <Occhiello>Foto</Occhiello>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-3 tracking-tight">Immagini per uso editoriale</h2>
            <p className="text-base text-gray-600 dark:text-gray-400 mb-8">
              Pubblicabili citando «UNASCA Studi». Le foto degli eventi sono nella{' '}
              <Link to="/gallery" className="font-semibold text-primary-700 hover:underline dark:text-primary-400">galleria</Link>;
              per altre immagini ad alta risoluzione scrivi al contatto stampa.
            </p>
            <div className="grid gap-5 md:grid-cols-2">
              {pressKit.foto.map((f) => (
                <figure key={f.file} className={`${CARD} overflow-hidden`}>
                  <img src={f.file} alt={f.titolo} loading="lazy" className="w-full h-auto" />
                  <figcaption className="flex items-center justify-between gap-3 p-4">
                    <span className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                      <IconaImmagine className="h-4 w-4 text-primary-700 dark:text-primary-400" aria-hidden="true" /> {f.titolo}
                    </span>
                    <a href={f.file} download className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-gray-700 ring-1 ring-gray-200 hover:ring-primary-300 dark:text-gray-200 dark:ring-gray-700">
                      <Download className="h-3.5 w-3.5" aria-hidden="true" /> Scarica
                    </a>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* ═══ BADGE ═══ */}
        <section id="badge" className="scroll-mt-32 py-16 md:py-20 bg-white dark:bg-gray-900">
          <div className="container-custom max-w-5xl">
            <Occhiello>Per gli associati</Occhiello>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-3 tracking-tight">Il badge per il sito del tuo studio</h2>
            <p className="text-base text-gray-600 dark:text-gray-400 mb-8 max-w-3xl">
              Gli associati UNASCA in regola possono mostrare sul proprio sito il badge ufficiale. Gli studi della Rete Certificata
              hanno un badge dedicato che porta i clienti direttamente alla scheda dello studio sulla mappa.
            </p>
            <div className="grid gap-5 md:grid-cols-2">
              {([
                { famiglia: 'associato' as const, titolo: 'Associato UNASCA', testo: 'Per tutti gli associati in regola. Porta al sito di UNASCA Studi.' },
                { famiglia: 'rete-certificata' as const, titolo: 'Associato UNASCA · Rete Certificata', testo: 'Per gli studi certificati. Porta alla scheda dello studio sulla mappa.' }
              ]).map(({ famiglia, titolo, testo }) => (
                <div key={famiglia} className={`${CARD} overflow-hidden`}>
                  <div className="grid grid-cols-1">
                    {(['chiaro', 'scuro'] as const).map((tema) => (
                      <div key={tema} className={`flex items-center justify-center px-6 py-5 ${tema === 'scuro' ? 'bg-gray-900' : 'bg-gray-50 dark:bg-gray-100'}`}>
                        <img
                          src={fileBadge(famiglia, 'orizzontale', tema)}
                          width={FORMATI.orizzontale.larghezza}
                          height={FORMATI.orizzontale.altezza}
                          alt={`${titolo}, sfondo ${tema}`}
                          loading="lazy"
                          className="h-auto w-full max-w-[300px]"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="p-5">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">{titolo}</h3>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{testo}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4 rounded-2xl bg-primary-50 p-5 dark:bg-primary-900/20">
              <BadgeCheck className="h-8 w-8 flex-shrink-0 text-primary-700 dark:text-primary-400" aria-hidden="true" />
              <p className="flex-1 text-sm text-gray-700 dark:text-gray-300">
                Sei associato? Nell'area riservata trovi il tuo badge in tre formati, chiaro e scuro, con il codice già pronto da incollare.
              </p>
              <Link to={ctaBadge} className="inline-flex flex-shrink-0 items-center justify-center gap-2 rounded-xl bg-primary-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-800 transition-colors">
                Prendi il tuo badge <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>

        {/* ═══ CONTATTI ═══ */}
        <section id="contatti" className="scroll-mt-32 relative overflow-hidden bg-gradient-to-r from-primary-950 via-primary-900 to-primary-800 text-white">
          <div
            className="absolute inset-0 opacity-10 text-primary-300"
            style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)', backgroundSize: '24px 24px' }}
            aria-hidden="true"
          />
          <div className="relative container-custom max-w-5xl py-16 md:py-20">
            <Occhiello><span className="text-primary-300">Contatto stampa</span></Occhiello>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-3">Serve altro?</h2>
            <p className="text-base md:text-lg text-white/80 max-w-2xl mb-8">
              Interviste, dati, chiarimenti o foto ad alta risoluzione: scrivici, rispondiamo {contatto.risposta}.
            </p>
            <div className="grid gap-4 sm:grid-cols-3">
              <a href={`mailto:${contatto.email}`} className="rounded-2xl bg-white/10 p-5 ring-1 ring-white/15 hover:bg-white/15 transition-colors">
                <Mail className="h-5 w-5 text-primary-300" aria-hidden="true" />
                <p className="mt-3 text-xs uppercase tracking-wider text-white/60">Email</p>
                <p className="text-sm font-semibold">{contatto.email}</p>
              </a>
              <a href={`tel:${contatto.telefono.replace(/[^\d+]/g, '')}`} className="rounded-2xl bg-white/10 p-5 ring-1 ring-white/15 hover:bg-white/15 transition-colors">
                <Phone className="h-5 w-5 text-primary-300" aria-hidden="true" />
                <p className="mt-3 text-xs uppercase tracking-wider text-white/60">Telefono</p>
                <p className="text-sm font-semibold">{contatto.telefono}</p>
              </a>
              <div className="rounded-2xl bg-white/10 p-5 ring-1 ring-white/15">
                <MapPin className="h-5 w-5 text-primary-300" aria-hidden="true" />
                <p className="mt-3 text-xs uppercase tracking-wider text-white/60">Sede</p>
                <p className="text-sm font-semibold">{sintesi.sede}</p>
              </div>
            </div>
            <p className="mt-10 text-xs text-white/60 max-w-3xl">
              Nota sui marchi: il logo UNASCA e il timbro della Rete Certificata si possono usare solo per parlare di UNASCA e delle
              sue attività. I badge sono riservati agli associati aventi diritto.
            </p>
          </div>
        </section>
      </div>
    </>
  );
};

export default PressKitPage;
